import {
  Component,
  OnChanges,
  Input,
  ChangeDetectorRef,
  SimpleChanges,
  ChangeDetectionStrategy,
  output,
  inject,
} from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import {
  ReactiveComponent,
  FileV2Service,
  FileV2Info,
  FileUploadV2Module,
  IconV2Module,
  SpinnerV2Module,
} from '@schenck/rotec-components';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'ma-task-documents',
  templateUrl: './task-documents.component.html',
  styleUrls: ['./task-documents.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileUploadV2Module, IconV2Module, SpinnerV2Module, AsyncPipe],
})
export class TaskDocumentsComponent extends ReactiveComponent implements OnChanges {
  private readonly fileService = inject(FileV2Service);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() public documentIds: string[];
  @Input() public documentsLoading: boolean;
  public readonly documentIdsChange = output<string[]>();

  public taskDocuments$: Observable<FileV2Info[]>;
  public loading = false;

  constructor() {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.documentIds) {
      this.setTaskDocs();
    }
  }

  private setTaskDocs(): void {
    const docs$ = (this.documentIds || []).map((id) => this.fileService.getMeta$(id));
    this.taskDocuments$ = combineLatest(docs$).pipe(startWith([]));
  }

  public openFile(id: string): void {
    this.fileService.open(id);
  }

  public downloadFile(id: string, name: string, event: Event): void {
    event.stopPropagation();
    this.fileService.download(id, name);
  }

  public deleteFile(id: string, event: Event): void {
    this.loading = true;
    event.stopPropagation();
    this.fileService
      .delete$(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.documentIdsChange.emit(this.documentIds.filter((docId) => docId !== id));
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  public uploadFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }

    this.loading = true;
    const fileIds$ = files.map((file) => this.fileService.create$(file));
    combineLatest(fileIds$)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((ids) => {
        this.documentIdsChange.emit([...this.documentIds, ...ids]);
        this.loading = false;
        this.cdr.markForCheck();
      });
  }
}
